import {
  Download,
  Filter,
  Plus,
  RefreshCw,
  Search,
  Upload,
  X,
} from "lucide-react";
import type React from "react";
import { forwardRef, memo, useEffect, useRef, useState } from "react";
import { strings } from "@/I18n";
import { cn } from "@/lib/utils";
import { TEST_IDS } from "@/v2/constants/testIds";
import { FilterChip } from "@/v2/ui/composed/filters/FilterChip";
import { Button } from "@/v2/ui/primitives/button";
import { Input } from "@/v2/ui/primitives/input";
import { Separator } from "@/v2/ui/primitives/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/v2/ui/primitives/tooltip";

export interface SearchBarWithActionsProps {
  /** Search input value */
  value: string;
  /** Callback when search value changes */
  onChange: (value: string) => void;
  /** Placeholder text for search input */
  placeholder?: string;
  /** Callback when search is cleared */
  onClear?: () => void;
  /** Debounce delay in milliseconds */
  debounce?: number;
  /** Additional class name */
  className?: string;

  /** Page title displayed on the left */
  title?: string;

  /** Node rendered in the left cluster, for pages whose heading is not a plain title */
  leading?: React.ReactNode;

  /** Optional tabs to render before the title */
  tabs?: React.ReactNode;

  /** Show filter button */
  showFilterButton?: boolean;
  /** Callback when filter button is clicked */
  onFilter?: () => void;
  /** Number of active filters (shows badge) */
  activeFilterCount?: number;

  /** Show add/create button */
  showAddButton?: boolean;
  /** Callback when add button is clicked */
  onAdd?: () => void;
  /** Add button tooltip text */
  addButtonLabel?: string;
  /** Disable add button */
  addButtonDisabled?: boolean;

  /** Show refresh button */
  showRefreshButton?: boolean;
  /** Callback when refresh button is clicked */
  onRefresh?: () => void;

  /** Show export button */
  showExportButton?: boolean;
  /** Callback when export button is clicked */
  onExport?: () => void;

  /** Show import button */
  showImportButton?: boolean;
  /** Callback when import button is clicked */
  onImport?: () => void;

  /** Custom actions menu rendered in place of the export/import icon buttons */
  actionsMenu?: React.ReactNode;

  /** Active filter chips to display */
  filters?: Array<{
    label: string;
    value: string;
    tags?: { name: string; color: string }[];
  }>;
  /** Callback when a filter chip is removed */
  onRemoveFilter?: (index: number) => void;

  /** Additional actions to render */
  extraActions?: React.ReactNode;

  /** Data attributes for tour targeting */
  dataTour?: {
    title?: string;
    search?: string;
    filter?: string;
  };
}

const SearchBarWithActionsComponent = forwardRef<
  HTMLDivElement,
  SearchBarWithActionsProps
>(
  (
    {
      value,
      onChange,
      placeholder = strings("table.search"),
      onClear,
      debounce,
      className,
      title,
      leading,
      tabs,
      showFilterButton,
      onFilter,
      activeFilterCount = 0,
      showAddButton,
      onAdd,
      addButtonLabel = strings("common.addNew"),
      addButtonDisabled,
      showRefreshButton,
      onRefresh,
      showExportButton,
      onExport,
      showImportButton,
      onImport,
      actionsMenu,
      filters,
      onRemoveFilter,
      extraActions,
      dataTour,
    },
    ref
  ) => {
    const [localValue, setLocalValue] = useState(value);
    const debounceTimerRef = useRef<NodeJS.Timeout>();

    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);

      if (debounce && debounce > 0) {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
          onChange(newValue);
        }, debounce);
      } else {
        onChange(newValue);
      }
    };

    const handleClear = () => {
      setLocalValue("");
      onChange("");
      if (onClear) {
        onClear();
      }
    };

    useEffect(() => {
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, []);

    const hasActions =
      showFilterButton ||
      showAddButton ||
      showRefreshButton ||
      showExportButton ||
      showImportButton ||
      Boolean(actionsMenu);

    return (
      <TooltipProvider delayDuration={300}>
        <div ref={ref} className={cn("tw-space-y-4", className)}>
          <div className="tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
            <div className="tw-flex tw-items-center tw-gap-4">
              {leading}
              {title && (
                <h1
                  className="tw-text-2xl tw-font-bold tw-tracking-tight tw-text-foreground tw-m-0"
                  data-tour={dataTour?.title}
                >
                  {title}
                </h1>
              )}
              {tabs}
            </div>

            <div className="tw-flex tw-flex-1 tw-items-center tw-gap-2 sm:tw-max-w-2xl sm:tw-ml-auto">
              <div
                className="tw-relative tw-flex-1"
                data-tour={dataTour?.search}
              >
                <Search className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-h-4 tw-w-4 tw-text-muted-foreground tw-pointer-events-none" />
                <Input
                  type="text"
                  value={localValue}
                  onChange={handleChange}
                  placeholder={placeholder}
                  className="tw-pl-9 tw-pr-9 tw-h-10"
                  data-testid={TEST_IDS.searchInput}
                />
                {localValue && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-h-4 tw-w-4 tw-text-muted-foreground hover:tw-text-foreground tw-transition-colors"
                  >
                    <X className="tw-h-4 tw-w-4" />
                    <span className="tw-sr-only">
                      {strings("common.clearSearch")}
                    </span>
                  </button>
                )}
              </div>

              {hasActions && (
                <div className="tw-flex tw-items-center tw-gap-1">
                  {showFilterButton && (
                    <Tooltip>
                      <TooltipTrigger asChild={true}>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={onFilter}
                          className="tw-relative tw-h-10 tw-w-10"
                          data-tour={dataTour?.filter}
                          data-testid={TEST_IDS.filterButton}
                        >
                          <Filter className="tw-h-4 tw-w-4" />
                          {activeFilterCount > 0 && (
                            <span className="tw-absolute tw--top-1 tw--right-1 tw-h-5 tw-w-5 tw-rounded-full tw-bg-primary tw-text-primary-foreground tw-text-xs tw-font-medium tw-flex tw-items-center tw-justify-center">
                              {activeFilterCount}
                            </span>
                          )}
                          <span className="tw-sr-only">
                            {strings("title.filter")}
                          </span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{strings("title.filter")}</TooltipContent>
                    </Tooltip>
                  )}

                  {showRefreshButton && (
                    <Tooltip>
                      <TooltipTrigger asChild={true}>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={onRefresh}
                          className="tw-h-10 tw-w-10"
                          data-testid={TEST_IDS.refreshButton}
                        >
                          <RefreshCw className="tw-h-4 tw-w-4" />
                          <span className="tw-sr-only">
                            {strings("buttons.refresh")}
                          </span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {strings("buttons.refresh")}
                      </TooltipContent>
                    </Tooltip>
                  )}

                  {(showExportButton || showImportButton || actionsMenu) &&
                    (showFilterButton || showRefreshButton) && (
                      <Separator
                        orientation="vertical"
                        className="tw-h-6 tw-mx-1"
                      />
                    )}

                  {actionsMenu}

                  {!actionsMenu && showExportButton && (
                    <Tooltip>
                      <TooltipTrigger asChild={true}>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={onExport}
                          className="tw-h-10 tw-w-10"
                        >
                          <Download className="tw-h-4 tw-w-4" />
                          <span className="tw-sr-only">
                            {strings("common.export")}
                          </span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {strings("common.export")}
                      </TooltipContent>
                    </Tooltip>
                  )}

                  {!actionsMenu && showImportButton && (
                    <Tooltip>
                      <TooltipTrigger asChild={true}>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={onImport}
                          className="tw-h-10 tw-w-10"
                        >
                          <Upload className="tw-h-4 tw-w-4" />
                          <span className="tw-sr-only">
                            {strings("common.import")}
                          </span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {strings("common.import")}
                      </TooltipContent>
                    </Tooltip>
                  )}

                  {showAddButton && (
                    <>
                      {(showFilterButton ||
                        showRefreshButton ||
                        showExportButton ||
                        showImportButton ||
                        actionsMenu) && (
                        <Separator
                          orientation="vertical"
                          className="tw-h-6 tw-mx-1"
                        />
                      )}
                      <Tooltip>
                        <TooltipTrigger asChild={true}>
                          <Button
                            onClick={onAdd}
                            aria-label={addButtonLabel}
                            disabled={addButtonDisabled}
                            className="tw-h-10 tw-gap-2"
                          >
                            <Plus className="tw-h-4 tw-w-4" />
                            <span className="tw-hidden sm:tw-inline">
                              {addButtonLabel}
                            </span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="sm:tw-hidden">
                          {addButtonLabel}
                        </TooltipContent>
                      </Tooltip>
                    </>
                  )}
                </div>
              )}

              {extraActions}
            </div>
          </div>

          {filters && filters.length > 0 && (
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              {filters.map((filter, index) => (
                <FilterChip
                  key={`${filter.label}-${index}`}
                  label={filter.label}
                  value={filter.value}
                  tags={filter.tags}
                  onRemove={() => onRemoveFilter?.(index)}
                />
              ))}
            </div>
          )}
        </div>
      </TooltipProvider>
    );
  }
);

SearchBarWithActionsComponent.displayName = "SearchBarWithActions";

const SearchBarWithActions = memo(SearchBarWithActionsComponent);

export { SearchBarWithActions };
